import React, { useState, useRef, useEffect } from 'react';
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink';
import { 
  RoleSelectorContainer, 
  RoleSelectorButton, 
  RoleSelectorDropdown, 
  RoleOption, 
  RoleText, 
  RoleDescription, 
  OptionSeparator,
  CheckIcon 
} from './styles';
import './headerStyles.css';

export type UserRole = 'solucionador' | 'avaliador';

export interface RoleSelectorProps {
  selectedRole: UserRole;
  onRoleChange: (role: UserRole) => void;
  className?: string;
}

export const RoleSelector: React.FC<RoleSelectorProps> = ({
  selectedRole,
  onRoleChange,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const roleOptions = [
    {
      value: 'solucionador' as UserRole,
      label: 'Solucionador',
      description: 'Inscreve-se em programas abertos',
      icon: (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_4_1840)">
            <path d="M8.00008 1.66663C8.00008 1.66663 11.0001 3.02663 11.0001 8.66663C11.0001 10.3266 10.3067 12.38 9.93341 13.3333H6.06675C5.69341 12.38 5.00008 10.3266 5.00008 8.66663C5.00008 3.02663 8.00008 1.66663 8.00008 1.66663ZM9.33341 7.33329C9.33341 6.59996 8.73341 5.99996 8.00008 5.99996C7.26675 5.99996 6.66675 6.59996 6.66675 7.33329C6.66675 8.06663 7.26675 8.66663 8.00008 8.66663C8.73341 8.66663 9.33341 8.06663 9.33341 7.33329ZM5.12675 13.68C4.80675 12.86 4.11341 10.9 4.01341 9.09996L3.26008 9.59996C2.88675 9.85329 2.66675 10.2666 2.66675 10.7133V14.6666L5.12675 13.68ZM13.3334 14.6666V10.7133C13.3334 10.2666 13.1134 9.85329 12.7401 9.60663L11.9867 9.10663C11.8867 10.9 11.1867 12.8666 10.8734 13.6866L13.3334 14.6666Z" fill="#0066B3"/>
          </g>
          <defs>
            <clipPath id="clip0_4_1840">
              <rect width="16" height="16" fill="white"/>
            </clipPath>
          </defs>
        </svg>
      )
    },
    {
      value: 'avaliador' as UserRole,
      label: 'Avaliador',
      description: 'Avalie soluções atribuídas a você',
      icon: (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.6666 6.16004L9.87325 5.74671L7.99992 1.33337L6.12659 5.75337L1.33325 6.16004L4.97325 9.31337L3.87992 14L7.99992 11.5134L12.1199 14L11.0333 9.31337L14.6666 6.16004ZM7.99992 10.2667V4.06671L9.13992 6.76004L12.0599 7.01337L9.84659 8.93337L10.5133 11.7867L7.99992 10.2667Z" fill="#0066B3"/>
        </svg>
      )
    }
  ];

  const selectedOption = roleOptions.find(option => option.value === selectedRole);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (role: UserRole) => {
    onRoleChange(role);
    setIsOpen(false);
    
    // Navegar para a página apropriada baseada no perfil selecionado
    if (role === 'avaliador') {
      navigate('/hub/evaluator/challenge');
    } else {
      navigate('/hub/startup/challenge');
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <RoleSelectorContainer ref={dropdownRef} className={className} data-role="role-selector-container">
      <RoleSelectorButton onClick={handleToggle} isOpen={isOpen} data-role="role-selector-button">
        <span>{selectedOption?.label}</span>
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 16 16" 
          fill="none" 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        >
          <path 
            d="M4 6L8 10L12 6" 
            stroke={className?.includes('header') ? "#0066B3" : "#0066B3"}
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </RoleSelectorButton>

      {isOpen && (
        <RoleSelectorDropdown data-role="role-selector-dropdown">
          {roleOptions.map((option, index) => (
            <React.Fragment key={option.value}>
              <RoleOption 
                onClick={() => handleOptionSelect(option.value)}
                isSelected={selectedRole === option.value}
                data-role="role-option"
                className={selectedRole === option.value ? 'selected' : ''}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginTop: '5px' }}>
                  {option.icon}
                  <div>
                    <RoleText data-role="role-text">{option.label}</RoleText>
                    <RoleDescription data-role="role-description">{option.description}</RoleDescription>
                  </div>
                </div>
                {selectedRole === option.value && (
                  <CheckIcon>
                    <svg width="16"  height="16" viewBox="0 0 16 16" fill="none">
                      <path 
                        d="M13.3334 4L6.00008 11.3333L2.66675 8" 
                        stroke="#0066B3" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </CheckIcon>
                )}
              </RoleOption>
              {index < roleOptions.length - 1 && <OptionSeparator data-role="option-separator" />}
            </React.Fragment>
          ))}
        </RoleSelectorDropdown>
      )}
    </RoleSelectorContainer>
  );
};
